"use client";

import { useEffect, useId, useRef, useState } from "react";

// Click-to-load facade: Jotform (and the hCaptcha it pulls in) only loads once
// a visitor asks for the form, keeping it off every product page's initial load.
const FORM_ID = "260694480776066";
const FORM_URL = `https://form.jotform.com/${FORM_ID}`;
const EMBED_HANDLER_URL = "https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js";
const IFRAME_ID = `JotFormIFrame-${FORM_ID}`;
const DEEP_LINK_HASH = "#write-review";

const preconnect = () => {
  ["https://form.jotform.com", "https://cdn.jotfor.ms"].forEach((origin) => {
    if (document.head.querySelector(`link[rel="preconnect"][href="${origin}"]`)) return;
    const link = document.createElement("link");
    link.rel = "preconnect";
    link.href = origin;
    link.crossOrigin = "";
    document.head.appendChild(link);
  });
};

// Jotform's handler resizes the iframe to fit the form as it changes height.
const attachEmbedHandler = () => {
  const init = () => window.jotformEmbedHandler?.(`iframe[id='${IFRAME_ID}']`, "https://form.jotform.com/");

  if (window.jotformEmbedHandler) {
    init();
    return;
  }

  let script = document.querySelector(`script[src="${EMBED_HANDLER_URL}"]`);
  if (!script) {
    script = document.createElement("script");
    script.src = EMBED_HANDLER_URL;
    script.async = true;
    document.body.appendChild(script);
  }
  script.addEventListener("load", init, { once: true });
};

export default function JotformReviewEmbed() {
  const panelId = `review-form-${useId()}`;
  const [isOpen, setIsOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [openedVia, setOpenedVia] = useState(null);
  const wrapperRef = useRef(null);
  const iframeRef = useRef(null);

  // Deep link (e.g. a review-request email) opens the form automatically.
  useEffect(() => {
    const openFromHash = () => {
      if (window.location.hash !== DEEP_LINK_HASH || !wrapperRef.current) return;
      preconnect();
      setOpenedVia("link");
      setIsOpen(true);
      wrapperRef.current.scrollIntoView({ block: "start" });
    };

    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    return () => window.removeEventListener("hashchange", openFromHash);
  }, []);

  useEffect(() => {
    if (isOpen) attachEmbedHandler();
  }, [isOpen]);

  const handleOpen = () => {
    preconnect();
    setOpenedVia("click");
    setIsOpen(true);
  };

  const handleLoad = () => {
    setIsLoaded(true);
    if (openedVia === "click") iframeRef.current?.focus();
    // Re-align once the form has height (the mobile accordion may still have been expanding).
    if (openedVia === "link") wrapperRef.current?.scrollIntoView({ block: "start" });
  };

  return (
    <div ref={wrapperRef} className="review-form-facade">
      {!isOpen && (
        <>
          <button
            type="button"
            className="review-form-toggle"
            aria-expanded="false"
            aria-controls={panelId}
            onClick={handleOpen}
            onPointerEnter={preconnect}
            onFocus={preconnect}
          >
            Write a Review
          </button>
          <noscript>
            <a href={FORM_URL} target="_blank" rel="noopener noreferrer">
              Write a Review (opens in a new tab)
            </a>
          </noscript>
        </>
      )}

      {isOpen && (
        <div id={panelId} className="jotform-review-embed" aria-busy={!isLoaded}>
          {!isLoaded && (
            <div className="review-form-skeleton" role="status">
              Loading form…
            </div>
          )}
          <iframe
            ref={iframeRef}
            id={IFRAME_ID}
            title="Leave a Review"
            src={FORM_URL}
            onLoad={handleLoad}
            scrolling="no"
          />
        </div>
      )}
    </div>
  );
}
